import { Button } from "@/components/ui/button.tsx";
import { Checkbox } from "@/components/ui/checkbox";
import { useUpdateEscalation } from "@/lib/api/escalations.ts";
import { ArrowDownLeftIcon } from "@heroicons/react/24/outline";
import { Loader2 } from "lucide-react";
import { useState } from "react";

interface EscalationRiskAssessmentProps {
  menteeId: string;
  escalationId: string;
  onClose: () => void;
  onUpdate?: () => void;
}

interface ChecklistItem {
  id: string;
  label: string;
  question: string;
}

const CHECKLIST_ITEMS: ChecklistItem[] = [
  {
    id: "suicidal-intent",
    label: "Check suicidal intent",
    question: "Are you having thoughts of killing yourself?",
  },
  {
    id: "plan",
    label: "Ask teen for plan",
    question: "Have you thought about how you would kill yourself?",
  },
  {
    id: "method",
    label: "Ask teen for method",
    question: "What would you use to do it?",
  },
  {
    id: "means",
    label: "Ask teen for means",
    question: "Do you physically have those things right now?",
  },
];

export function EscalationRiskAssessment({
  menteeId,
  escalationId,
  onUpdate,
}: EscalationRiskAssessmentProps) {
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const { mutateAsync: updateEscalation, isPending } =
    useUpdateEscalation(menteeId);

  const handleUpdateToEmergency = async () => {
    try {
      await updateEscalation({
        id: escalationId,
        reason: "911",
      });
      onUpdate?.();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="space-y-4">
        <h2 className="font-avantt text-2xl font-bold leading-7 text-black">
          Please complete a risk assessment if needed
        </h2>
        <div className="text-base font-normal leading-snug text-[#777777]">
          Call 911 if your teen is an immediate danger to themselves or someone
          else.
        </div>

        <div className="mb-6 mt-6 flex flex-col gap-4">
          {CHECKLIST_ITEMS.map((item) => (
            <div key={item.id} className="flex flex-col gap-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={item.id}
                  checked={checkedItems.includes(item.id)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setCheckedItems([...checkedItems, item.id]);
                    } else {
                      setCheckedItems(
                        checkedItems.filter((id) => id !== item.id),
                      );
                    }
                  }}
                />
                <label
                  htmlFor={item.id}
                  className="text-lg font-bold leading-snug text-black"
                >
                  {item.label}
                </label>
              </div>

              <div className="mt-1 flex items-center justify-between gap-2">
                <p className="border-l-2 pl-3 text-base font-normal leading-snug text-black">
                  &quot;{item.question}&quot;
                </p>
                <ArrowDownLeftIcon className="h-4 w-4 text-black" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button
        variant="destructive"
        size="lg"
        className="mt-12 w-full rounded-xl bg-red-600 px-6 py-6 text-xl font-bold hover:bg-red-700"
        onClick={handleUpdateToEmergency}
        disabled={isPending}
      >
        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        My Teen Is Now In Crisis
      </Button>
    </div>
  );
}
